'use client';

import { useEffect, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';

export const STATIC_EXPORT_PLACEHOLDERS = new Set([
  '__default',
  '_default',
  'default',
  '0',
  'null',
  'undefined',
]);

const WORKSPACE_EVENT_ID_KEY = 'eh:workspace-event-id';
const WORKSPACE_ACTIVITY_ID_KEY = 'eh:workspace-activity-id';
const WORKSPACE_ACTIVITY_EVENT_ID_KEY = 'eh:workspace-activity-event-id';
const WORKSPACE_FORM_ID_KEY = 'eh:workspace-form-id';
const WORKSPACE_RESPONSE_ID_KEY = 'eh:workspace-response-id';

/** Clear event/form workspace IDs that must not survive logout or user switch. */
export function clearWorkspaceSessionContext(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(WORKSPACE_EVENT_ID_KEY);
    sessionStorage.removeItem(WORKSPACE_ACTIVITY_ID_KEY);
    sessionStorage.removeItem(WORKSPACE_ACTIVITY_EVENT_ID_KEY);
    sessionStorage.removeItem(WORKSPACE_FORM_ID_KEY);
    sessionStorage.removeItem(WORKSPACE_RESPONSE_ID_KEY);
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(`${WORKSPACE_FORM_ID_KEY}:name:`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore quota / private mode
  }
}

export function rememberWorkspaceEventId(eventId: string): void {
  if (typeof window === 'undefined' || !isValidEventWorkspaceId(eventId)) {
    return;
  }
  try {
    sessionStorage.setItem(WORKSPACE_EVENT_ID_KEY, eventId);
  } catch {
    // ignore quota / private mode
  }
}

export function readRememberedWorkspaceEventId(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const value = sessionStorage.getItem(WORKSPACE_EVENT_ID_KEY);
    return isValidEventWorkspaceId(value) ? String(value) : '';
  } catch {
    return '';
  }
}

/** When soft-nav rewrote the URL to `__default`, put the real id back in the address bar. */
function repairPlaceholderEventIdInUrl(eventId: string): void {
  if (typeof window === 'undefined' || !isValidEventWorkspaceId(eventId)) {
    return;
  }
  const { pathname, search, hash } = window.location;
  const parts = pathname.split('/');
  // ['', 'dashboard', 'events', '__default', ...]
  if (parts[1] !== 'dashboard' || parts[2] !== 'events' || !isStaticExportPlaceholder(parts[3])) {
    return;
  }
  parts[3] = eventId;
  const nextPath = parts.join('/') || '/';
  window.history.replaceState(window.history.state, '', `${nextPath}${search}${hash}`);
}

export function rememberWorkspaceActivityId(activityId: string, eventId?: string): void {
  if (typeof window === 'undefined' || !isValidEventWorkspaceId(activityId)) {
    return;
  }
  try {
    sessionStorage.setItem(WORKSPACE_ACTIVITY_ID_KEY, activityId);
    if (eventId && isValidEventWorkspaceId(eventId)) {
      sessionStorage.setItem(WORKSPACE_ACTIVITY_EVENT_ID_KEY, eventId);
    }
  } catch {
    // ignore quota / private mode
  }
}

export function readRememberedWorkspaceActivityId(expectedEventId?: string): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    // If an expectedEventId is provided or can be resolved from the current path:
    // it must match the event that the activity was saved for.
    // If the event cannot be verified (e.g. expectedEventId is missing/invalid,
    // or mismatched), never return an unverified activity ID.
    const expectedEvent = expectedEventId || resolveEventIdFromPath();
    if (!expectedEvent || !isValidEventWorkspaceId(expectedEvent)) {
      return '';
    }

    const savedEventId = sessionStorage.getItem(WORKSPACE_ACTIVITY_EVENT_ID_KEY);
    if (!savedEventId || savedEventId.toLowerCase() !== expectedEvent.toLowerCase()) {
      return '';
    }

    const value = sessionStorage.getItem(WORKSPACE_ACTIVITY_ID_KEY);
    return isValidEventWorkspaceId(value) ? String(value) : '';
  } catch {
    return '';
  }
}

function repairPlaceholderActivityIdInUrl(activityId: string): void {
  if (typeof window === 'undefined' || !isValidEventWorkspaceId(activityId)) {
    return;
  }
  const { pathname, search, hash } = window.location;
  const parts = pathname.split('/');
  const actIdx = parts.indexOf('activities');
  if (actIdx < 0 || !isStaticExportPlaceholder(parts[actIdx + 1])) {
    return;
  }
  parts[actIdx + 1] = activityId;
  const nextPath = parts.join('/') || '/';
  window.history.replaceState(window.history.state, '', `${nextPath}${search}${hash}`);
}

export const EVENT_WORKSPACE_ROUTE_SEGMENTS = new Set([
  'activities',
  'analytics',
  'announcements',
  'attendance',
  'attendees',
  'bank-details',
  'certificates',
  'chat',
  'communications',
  'conference',
  'contact-attendees',
  'credentials',
  'discovery',
  'edit',
  'email-campaigning',
  'evaluation',
  'event-info',
  'event-settlements',
  'event-type-module',
  'feedback',
  'finance',
  'financials',
  'ideathon',
  'info',
  'inbox',
  'intelligence',
  'jury',
  'live',
  'manage',
  'marketing',
  'mun',
  'operations',
  'orders',
  'organizer-team',
  'overview',
  'participants',
  'permission-letter',
  'payments-taxes',
  'pitch',
  'portal',
  'quizzes',
  'registration-form',
  'reports',
  'results',
  'sale-window',
  'schedule',
  'sessions',
  'settings',
  'simulation',
  'speaker',
  'speakers',
  'sponsors',
  'submissions',
  'support',
  'team',
  'team-management',
  'teams',
  'tickets',
  'visibility',
  'volunteers',
  'waitlist',
]);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isStaticExportPlaceholder(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }

  return STATIC_EXPORT_PLACEHOLDERS.has(String(value).toLowerCase());
}

export function isReservedEventWorkspaceSegment(value: string | null | undefined): boolean {
  return Boolean(value && EVENT_WORKSPACE_ROUTE_SEGMENTS.has(String(value).toLowerCase()));
}

export function isValidEventWorkspaceId(value: string | null | undefined): boolean {
  if (!value || isStaticExportPlaceholder(value) || isReservedEventWorkspaceSegment(value)) {
    return false;
  }

  return UUID_PATTERN.test(String(value));
}

function segmentFromPath(pathname: string, anchor: string, offset = 1): string {
  const parts = pathname.split('/').filter(Boolean);
  const index = parts.indexOf(anchor);
  if (index < 0) {
    return '';
  }

  const value = parts[index + offset] ?? '';
  return isStaticExportPlaceholder(value) ? '' : value;
}

/**
 * Resolve the dynamic segment that follows `anchor` in the URL path.
 * Static export serves `/…/__default/…` shells, so the build-time route param
 * is a placeholder in production; prefer the real segment from the browser URL.
 */
export function resolveSegmentAfter(
  anchor: string,
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const paths = [
    pathname ?? '',
    typeof window !== 'undefined' ? window.location.pathname : '',
  ].filter(Boolean);

  for (const path of paths) {
    const value = segmentFromPath(path, anchor);
    if (value) {
      return value;
    }
  }

  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  return param && !isStaticExportPlaceholder(param) ? String(param) : '';
}

/**
 * Resolve event id/slug when static export serves `/…/__default/…` shells.
 * Prefer the real segment from the browser URL over build-time route params.
 */
export function resolveEventIdFromPath(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const fromDashboard = segmentFromPath(path, 'events');
    if (isValidEventWorkspaceId(fromDashboard)) {
      return fromDashboard;
    }
  }

  if (isValidEventWorkspaceId(param)) {
    return String(param);
  }

  return '';
}

export function resolveEventSlugFromPath(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  if (param && !isStaticExportPlaceholder(param)) {
    return String(param);
  }

  const paths = [
    pathname ?? '',
    typeof window !== 'undefined' ? window.location.pathname : '',
  ].filter(Boolean);

  for (const path of paths) {
    const slug = segmentFromPath(path, 'events');
    if (slug) {
      return slug;
    }
  }

  return '';
}

export function useResolvedEventId(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.eventId;
  const fromPath = resolveEventIdFromPath(
    Array.isArray(param) ? param[0] : (param as string | undefined),
    pathname,
  );
  const [recoveredId, setRecoveredId] = useState(() => {
    if (fromPath) return '';
    return readRememberedWorkspaceEventId();
  });

  useEffect(() => {
    if (fromPath) {
      rememberWorkspaceEventId(fromPath);
      setRecoveredId('');
      return;
    }

    const cached = readRememberedWorkspaceEventId();
    if (!cached || !pathname?.includes('/dashboard/events/')) {
      setRecoveredId('');
      return;
    }

    setRecoveredId(cached);
    repairPlaceholderEventIdInUrl(cached);
  }, [fromPath, pathname]);

  return fromPath || recoveredId;
}

export function resolveActivityIdFromPath(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const fromPath = segmentFromPath(path, 'activities');
    if (isValidEventWorkspaceId(fromPath)) {
      return fromPath;
    }
  }

  if (isValidEventWorkspaceId(param)) {
    return String(param);
  }

  return '';
}

export function useResolvedActivityId(eventId?: string): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.activityId;

  const currentEventId = eventId || resolveEventIdFromPath(
    Array.isArray(params?.eventId) ? params.eventId[0] : (params?.eventId as string | undefined),
    pathname,
  );

  const fromPath = resolveActivityIdFromPath(
    Array.isArray(param) ? param[0] : (param as string | undefined),
    pathname,
  );
  const [recoveredId, setRecoveredId] = useState(() => {
    if (fromPath) return '';
    return readRememberedWorkspaceActivityId(currentEventId);
  });

  useEffect(() => {
    if (fromPath) {
      rememberWorkspaceActivityId(fromPath, currentEventId);
      setRecoveredId('');
      return;
    }

    if (!currentEventId || !isValidEventWorkspaceId(currentEventId) || !pathname?.includes('/activities/')) {
      setRecoveredId('');
      return;
    }

    const cached = readRememberedWorkspaceActivityId(currentEventId);
    if (!cached) {
      setRecoveredId('');
      return;
    }

    setRecoveredId(cached);
    repairPlaceholderActivityIdInUrl(cached);
  }, [fromPath, pathname, currentEventId]);

  return fromPath || recoveredId;
}

export function isActivityWorkspacePath(pathname: string | null | undefined): boolean {
  const paths = [
    pathname ?? '',
    typeof window !== 'undefined' ? window.location.pathname : '',
  ].filter(Boolean);

  for (const path of paths) {
    const parts = path.split('/').filter(Boolean);
    const actIdx = parts.indexOf('activities');
    if (actIdx >= 0 && parts[0] === 'dashboard' && parts[1] === 'events') {
      const actSegment = parts[actIdx + 1];
      if (actSegment && actSegment.length > 0) {
        return true;
      }
    }
  }

  return false;
}

export function useResolvedConferenceId(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.id;
  const raw = Array.isArray(param) ? param[0] : (param as string | undefined);
  if (raw && !isStaticExportPlaceholder(raw)) {
    return raw;
  }
  const paths = [
    pathname ?? '',
    typeof window !== 'undefined' ? window.location.pathname : '',
  ].filter(Boolean);
  for (const path of paths) {
    const val = segmentFromPath(path, 'conferences');
    if (val) return val;
  }
  return '';
}

export function useResolvedEventSlug(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.slug;

  return resolveEventSlugFromPath(
    Array.isArray(param) ? param[0] : (param as string | undefined),
    pathname,
  );
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * DAVS public verify URLs are `/v/<reference>` and the reference itself may
 * contain slashes (`EH/INT/2026/0001`). Static export serves `/v/__default/`.
 */
export function resolveDavsVerifyReference(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const fromParam = Array.isArray(paramValue)
    ? paramValue.filter(Boolean).map(decodePathSegment).join('/')
    : decodePathSegment(String(paramValue ?? ''));
  if (fromParam && !isStaticExportPlaceholder(fromParam)) {
    return fromParam.replace(/\/+$/, '');
  }

  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const parts = path.split('/').filter(Boolean);
    const index = parts.indexOf('v');
    if (index < 0) continue;
    const reference = parts
      .slice(index + 1)
      .filter((part) => part && !isStaticExportPlaceholder(part))
      .map(decodePathSegment)
      .join('/')
      .replace(/\/+$/, '');
    if (reference) return reference;
  }

  return '';
}

const PUBLIC_FORM_SLUG_KEY = 'eh:public-form-slug';
const FORM_RESERVED_SEGMENTS = new Set(['create', 'templates', 'archived']);

function rememberWorkspaceFormId(formId: string): void {
  if (typeof window === 'undefined' || !isValidFormWorkspaceId(formId)) {
    return;
  }
  try {
    sessionStorage.setItem(WORKSPACE_FORM_ID_KEY, formId);
  } catch {
    // ignore quota / private mode
  }
}

function readRememberedWorkspaceFormId(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const value = sessionStorage.getItem(WORKSPACE_FORM_ID_KEY);
    return isValidFormWorkspaceId(value) ? String(value) : '';
  } catch {
    return '';
  }
}

function repairPlaceholderFormIdInUrl(formId: string): void {
  if (typeof window === 'undefined' || !isValidFormWorkspaceId(formId)) {
    return;
  }
  const { pathname, search, hash } = window.location;
  const parts = pathname.split('/');
  if (parts[1] !== 'dashboard' || parts[2] !== 'forms' || !isStaticExportPlaceholder(parts[3])) {
    return;
  }
  parts[3] = formId;
  const nextPath = parts.join('/') || '/';
  window.history.replaceState(window.history.state, '', `${nextPath}${search}${hash}`);
}

export function isValidFormWorkspaceId(value: string | null | undefined): boolean {
  if (!value || isStaticExportPlaceholder(value) || FORM_RESERVED_SEGMENTS.has(String(value).toLowerCase())) {
    return false;
  }
  return UUID_PATTERN.test(String(value));
}

function resolveFormIdFromPath(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const fromPath = segmentFromPath(path, 'forms');
    if (isValidFormWorkspaceId(fromPath)) {
      return fromPath;
    }
  }

  if (isValidFormWorkspaceId(param)) {
    return String(param);
  }

  return '';
}

function resolveFormIdNow(
  paramValue?: string | string[] | null,
  routerPathname?: string | null,
): string {
  const browserPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const fromBrowser = resolveFormIdFromPath(paramValue, browserPath);
  if (fromBrowser) {
    return fromBrowser;
  }
  const fromRouter = resolveFormIdFromPath(paramValue, routerPathname);
  if (fromRouter) {
    return fromRouter;
  }
  return readRememberedWorkspaceFormId();
}

function rememberWorkspaceFormName(formId: string, name: string): void {
  if (typeof window === 'undefined' || !isValidFormWorkspaceId(formId) || !name.trim()) {
    return;
  }
  try {
    sessionStorage.setItem(`${WORKSPACE_FORM_ID_KEY}:name:${formId}`, name.trim());
  } catch {
    // ignore quota / private mode
  }
}

function readRememberedWorkspaceFormName(formId: string): string {
  if (typeof window === 'undefined' || !isValidFormWorkspaceId(formId)) {
    return '';
  }
  try {
    return sessionStorage.getItem(`${WORKSPACE_FORM_ID_KEY}:name:${formId}`) || '';
  } catch {
    return '';
  }
}

export function useResolvedFormId(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = Array.isArray(params?.formId) ? params.formId[0] : (params?.formId as string | undefined);

  const [formId, setFormId] = useState(() => {
    const resolved = resolveFormIdNow(param, pathname);
    if (resolved && typeof window !== 'undefined') {
      rememberWorkspaceFormId(resolved);
      repairPlaceholderFormIdInUrl(resolved);
    }
    return resolved;
  });

  useEffect(() => {
    const resolved = resolveFormIdNow(param, pathname);
    if (resolved) {
      rememberWorkspaceFormId(resolved);
      repairPlaceholderFormIdInUrl(resolved);
      setFormId(resolved);
      return;
    }
    setFormId('');
  }, [param, pathname]);

  return formId;
}

export function useResolvedFormName(formId: string, fallback: string): string {
  const [name, setName] = useState(() => (formId ? readRememberedWorkspaceFormName(formId) : ''));

  useEffect(() => {
    if (!formId) {
      setName('');
      return;
    }
    const cached = readRememberedWorkspaceFormName(formId);
    if (cached) {
      setName(cached);
    }
  }, [formId]);

  return name || fallback;
}

export function cacheWorkspaceFormName(formId: string, name: string): void {
  rememberWorkspaceFormName(formId, name);
}

export function resolveResponseIdFromPath(
  paramValue?: string | string[] | null,
  pathname?: string | null,
): string {
  const param = Array.isArray(paramValue) ? paramValue[0] : paramValue;
  if (param && !isStaticExportPlaceholder(param) && UUID_PATTERN.test(String(param))) {
    return String(param);
  }

  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const parts = path.split('/').filter(Boolean);
    const idx = parts.indexOf('responses');
    if (idx < 0) continue;
    const val = parts[idx + 1] ?? '';
    if (val && !isStaticExportPlaceholder(val) && UUID_PATTERN.test(val)) {
      return val;
    }
  }

  return '';
}

export function useResolvedResponseId(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.responseId;
  const fromPath = resolveResponseIdFromPath(
    Array.isArray(param) ? param[0] : (param as string | undefined),
    pathname,
  );
  const [recoveredId, setRecoveredId] = useState('');

  useEffect(() => {
    if (fromPath) {
      try {
        sessionStorage.setItem(WORKSPACE_RESPONSE_ID_KEY, fromPath);
      } catch {
        // ignore
      }
      setRecoveredId('');
      return;
    }

    let cached = '';
    try {
      const value = sessionStorage.getItem(WORKSPACE_RESPONSE_ID_KEY);
      cached = value && UUID_PATTERN.test(value) ? value : '';
    } catch {
      cached = '';
    }

    if (!cached || !pathname?.includes('/responses/')) {
      setRecoveredId('');
      return;
    }

    setRecoveredId(cached);

    if (typeof window !== 'undefined' && isStaticExportPlaceholder(pathname?.split('/').pop())) {
      const parts = window.location.pathname.split('/');
      const idx = parts.indexOf('responses');
      if (idx >= 0 && isStaticExportPlaceholder(parts[idx + 1])) {
        parts[idx + 1] = cached;
        const nextPath = parts.join('/') || '/';
        window.history.replaceState(window.history.state, '', `${nextPath}${window.location.search}${window.location.hash}`);
      }
    }
  }, [fromPath, pathname]);

  return fromPath || recoveredId;
}

function rememberPublicFormSlug(slug: string): void {
  if (typeof window === 'undefined' || !slug || isStaticExportPlaceholder(slug)) {
    return;
  }
  try {
    sessionStorage.setItem(PUBLIC_FORM_SLUG_KEY, slug);
  } catch {
    // ignore
  }
}

function readRememberedPublicFormSlug(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const cached = sessionStorage.getItem(PUBLIC_FORM_SLUG_KEY) || '';
    return cached && !isStaticExportPlaceholder(cached) ? cached : '';
  } catch {
    return '';
  }
}

/** Read public form slug from the browser URL (static export serves /f/__default/ shells). */
export function resolvePublicFormSlugFromBrowser(pathname?: string | null): string {
  const paths = [
    typeof window !== 'undefined' ? window.location.pathname : '',
    pathname ?? '',
  ].filter(Boolean);

  for (const path of paths) {
    const val = segmentFromPath(path, 'f');
    if (val && !isStaticExportPlaceholder(val)) {
      return val;
    }
  }

  for (const path of paths) {
    const match = path.match(/\/f\/([^/]+)/i);
    const val = match?.[1] ?? '';
    if (val && !isStaticExportPlaceholder(val)) {
      return val;
    }
  }

  return readRememberedPublicFormSlug();
}

export function useResolvedFormSlug(): string {
  const params = useParams();
  const pathname = usePathname();
  const param = params?.slug;
  const raw = Array.isArray(param) ? param[0] : (param as string | undefined);
  const [slug, setSlug] = useState('');

  useEffect(() => {
    const fromBrowser = resolvePublicFormSlugFromBrowser(pathname);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (fromBrowser) {
      setSlug(fromBrowser);
      rememberPublicFormSlug(fromBrowser);
      return;
    }

    if (raw && !isStaticExportPlaceholder(raw)) {
      setSlug(String(raw));
      rememberPublicFormSlug(String(raw));
      return;
    }

    const cached = readRememberedPublicFormSlug();
    if (cached && (pathname || '').includes('/f/')) {
      setSlug(cached);
    }
  }, [pathname, raw]);

  return slug;
}

